/** @jsx jsx */
/** @jsxFrag null */
import mean from 'lodash/mean';
import zip from 'lodash/zip';
import {jsx} from 'panel';

import {LENGTH, SIZE, TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {Placement} from 'common/components/tooltip/types';
import {renamePropertyValue} from 'common/query-builder/cohorts-query-builder/util';
import {LIFT_TOOLTIP_COMPARE, LiftType} from 'common/report/util/lift/types';
import {formatMetricNumber, formatPercent} from 'common/util';
import {formatDate, UNITS} from 'common/util/date';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {TooltipEl} from 'common/components/tooltip';

import {DEFAULT_LIFT_COLUMN_WIDTH} from '../constants';
import {
  CellRendererFunc,
  ColumnGroup,
  CommonDataTableFields,
  SegmentColumnSpec,
  TableColumn,
  TableColumnType,
  TableDataType,
  TableRow,
} from '../types';
import {TrendDataRow, ValueFormatterFunc} from './types';
import {formatDataTableNumber} from './utils';
import {GroupSortableTrendColumns} from './constants';

export function getTrendDataTableRow(
  row: TrendDataRow,
  liftComparisonValueGenerator?: (timestamp: number) => number,
): TableRow {
  const hasTimeComparison = Boolean(row.timeComparisonData);
  const hasLiftComparison = Boolean(row.liftComparisonData);
  // Don't show time comparison when we're in lift mode
  const showTimeComparison = hasTimeComparison && !hasLiftComparison;

  let liftComparisons = [];
  if (hasLiftComparison) {
    liftComparisons = row.liftComparisonData.map(({timestamp, value}) => [
      `${CommonDataTableFields.LiftComparisonDatePointPrefix}${timestamp}`,
      value,
    ]);

    if (liftComparisonValueGenerator) {
      liftComparisons = liftComparisons.concat(
        row.data.map(({timestamp}) => [
          `${CommonDataTableFields.LiftComparisonExtraFieldPrefix}${timestamp}`,
          liftComparisonValueGenerator(timestamp) || 0,
        ]),
      );
    }
  }

  const allDateValueMap = Object.fromEntries([
    ...row.data.map(({timestamp, value}) => [`${CommonDataTableFields.DatePointPrefix}${timestamp}`, value]),
    ...(showTimeComparison
      ? row.timeComparisonData.map(({timestamp, value}) => [
          `${CommonDataTableFields.TimeComparisonDatePointPrefix}${timestamp}`,
          value,
        ])
      : []),
    ...liftComparisons,
  ]);

  const segmentsMap = Object.fromEntries(
    row.segmentValues.map((segmentValue, i) => [`${CommonDataTableFields.SegmentPrefix}${i}`, segmentValue]),
  );

  return {
    identifier: row.rowId,
    checkedColor: row.selectionColor,
    isSelected: row.isSelected,
    ...segmentsMap,
    ...(Number.isFinite(row.cohortSize) ? {[CommonDataTableFields.CohortSize]: row.cohortSize} : {}),
    [CommonDataTableFields.Average]: mean(row.data.map(({value}) => value)),
    ...(showTimeComparison
      ? {
          [CommonDataTableFields.TimeComparisonAverage]: mean(row.timeComparisonData.map(({value}) => value)),
        }
      : {}),
    ...(hasLiftComparison
      ? {[CommonDataTableFields.LiftAverage]: mean(row.liftComparisonData.map((point) => point.value))}
      : {}),
    ...allDateValueMap,
  };
}

const HEADER_CUSTOM_DATE_FORMATTING = {
  [UNITS.minute]: `h:mma`,
  [UNITS.hour]: `MMM D, ha`,
  [UNITS.day]: `MMM D`,
  [UNITS.week]: `MMM D`,
  [UNITS.month]: `MMM YYYY`,
  [UNITS.quarter]: `[Q]Q YYYY`,
};

// TODO: Add unit tests for this
export function getTrendDataTableColumnGroups({
  segments,
  row, // only a single row is required to generate columns
  trendUnit,
  valueFormatter = formatDataTableNumber,
  liftType = null,
}: {
  segments: SegmentColumnSpec[];
  row: TrendDataRow;
  trendUnit: TimeUnit;
  valueFormatter?: ValueFormatterFunc;
  liftType?: LiftType;
}): ColumnGroup[] {
  const hasTimeComparison = Boolean(row.timeComparisonData);
  const hasLiftData = Boolean(row.liftComparisonData);
  // Don't show time comparison when we're in lift mode
  const showTimeComparison = hasTimeComparison && !hasLiftData;

  const CommonSegmentColumnFields = {
    isSortable: true,
    sticky: true,
    type: TableColumnType.Segment as const,
  };
  const segmentColumnGroups = segments.map((segment, i) => {
    const column = {
      ...CommonSegmentColumnFields,
      title: segment.title,
      field: `${CommonDataTableFields.SegmentPrefix}${i}`,
      hasOrdinalPrefix: i === 0,
      contentType: segment.segmentType,
      labelTransform: (row, field) => renamePropertyValue(row[field], segment.propertyName),
    };
    return {columns: [column]};
  });

  let averageColumnCellRenderer: CellRendererFunc = null;
  if (liftType === LiftType.Overall) {
    averageColumnCellRenderer = ({item, context}) => formatPercent(item[context.field]);
  } else if (valueFormatter) {
    averageColumnCellRenderer = ({item, context}) => valueFormatter(item[context.field], {isAverage: true});
  }

  const CommonAverageColumnFields = {
    isSortable: true,
    sticky: true,
    type: TableColumnType.Data as const,
    contentType: TableDataType.Number,
    ...(averageColumnCellRenderer ? {cellRenderer: averageColumnCellRenderer} : {}),
  };
  const averageField =
    liftType === LiftType.Overall ? CommonDataTableFields.LiftAverage : CommonDataTableFields.Average;
  const averageColumns: TableColumn[] = [
    {
      ...CommonAverageColumnFields,
      title: `Average`,
      field: averageField,
      ...(averageField in GroupSortableTrendColumns ? GroupSortableTrendColumns[averageField] : {}),
    },
    ...(showTimeComparison
      ? [
          {
            ...CommonAverageColumnFields,
            title: `Past Avg.`,
            field: CommonDataTableFields.TimeComparisonAverage,
          },
        ]
      : []),
  ];

  // Trim data and time comparison data to ensure both have equal number of date points, because
  // timezone offset changes (usually DST) can cause either of them to have fewer date points than the other.
  const pointCountDiff = hasTimeComparison ? row.timeComparisonData.length - row.data.length : 0;
  const equalLengthData = pointCountDiff === 0 ? row.data : row.data.slice(0, row.data.length + pointCountDiff);
  const equalLengthTimeComparisonData =
    pointCountDiff === 0
      ? row.timeComparisonData
      : row.timeComparisonData.slice(0, row.timeComparisonData.length - pointCountDiff);

  const allDatePoints = zip(equalLengthData, equalLengthTimeComparisonData);

  const CommonDateColumnFields = {
    isSortable: true,
    type: TableColumnType.Data as const,
    contentType: TableDataType.Number,
    ...(valueFormatter
      ? {cellRenderer: ({item, context}) => valueFormatter(item[context.field], {isAverage: false})}
      : {}),
  };
  const datePointColumnGroups: ColumnGroup[] = allDatePoints.map(
    ([datePoint, timeComparisonDatePoint]): ColumnGroup => {
      const columns: TableColumn[] = [
        {
          ...CommonDateColumnFields,
          title: formatDate(datePoint.timestamp, {
            unit: trendUnit,
            customFormatting: HEADER_CUSTOM_DATE_FORMATTING,
            displayRangeIfWeek: false,
          }),
          tooltip: formatDate(datePoint.timestamp, {unit: trendUnit}),
          field: `${CommonDataTableFields.DatePointPrefix}${datePoint.timestamp}`,
          ...(hasLiftData
            ? {
                liftField: `${CommonDataTableFields.LiftComparisonDatePointPrefix}${datePoint.timestamp}`,
                liftExtra: `${CommonDataTableFields.LiftComparisonExtraFieldPrefix}${datePoint.timestamp}`,
                sortTransform: (row) => {
                  return row[`${CommonDataTableFields.LiftComparisonDatePointPrefix}${datePoint.timestamp}`];
                },

                // TODO(reviewer) - this is really the only safe way to do this before the new visuals come out?
                // set width of data table cells to be slightly wider to allow for the extra lift pill
                initialWidth: DEFAULT_LIFT_COLUMN_WIDTH,
              }
            : {}),
          cellRenderer: getDataPointCellRenderer(valueFormatter, liftType, hasLiftData),
        },
      ];
      if (showTimeComparison) {
        columns.push({
          ...CommonDateColumnFields,
          title: `${formatDate(timeComparisonDatePoint.timestamp, {
            unit: trendUnit,
            customFormatting: HEADER_CUSTOM_DATE_FORMATTING,
            displayRangeIfWeek: false,
          })} (Past)`,
          tooltip: `${formatDate(timeComparisonDatePoint.timestamp, {unit: trendUnit})} (Past)`,
          field: `${CommonDataTableFields.TimeComparisonDatePointPrefix}${timeComparisonDatePoint.timestamp}`,
        });
      }
      return {columns};
    },
  );

  return [...segmentColumnGroups, {columns: averageColumns}, ...datePointColumnGroups];
}

function getDataPointCellRenderer(valueFormatter, liftType, hasLiftData): CellRendererFunc {
  return ({item, context}) => {
    if (hasLiftData) {
      if (liftType === LiftType.Overall) {
        const segmentValue = item[context.field];
        const overallSegmentValue = item[context.liftExtra];
        return (
          <>
            <div sel='.mp-table-cell'>
              {formatPercent(item[context.liftField])}
              <TooltipEl
                attrs={{
                  placement: Placement.Top,
                  text: `${valueFormatter(segmentValue, {isAverage: false})} ${LIFT_TOOLTIP_COMPARE} ${valueFormatter(
                    overallSegmentValue,
                    {
                      isAverage: false,
                    },
                  )}`,
                }}
              />
            </div>
          </>
        );
      }

      // Baseline and compare to past share the same logic
      const isPositive = item[context.liftField] > 0;
      // Have to fix it to two decimals and re-parse because of JS floating point number multiplication (otherwise, 2.99 * 100 becomes 299.99, etc)
      const percent = Number((100 * item[context.liftField]).toFixed(2));
      const liftAbsoluteValue = item[context.liftExtra];
      return (
        <>
          <div sel='.mp-table-cell'>
            {valueFormatter(item[context.field], {isAverage: false})}
            <TextHighlightEl
              sel='.mp-lift-pill'
              attrs={{
                text: `${isPositive ? `+` : ``}${formatMetricNumber(percent)}%`,
                theme: isPositive ? TEXT_HIGHLIGHT_THEME.POSITIVE : TEXT_HIGHLIGHT_THEME.NEGATIVE,
                size: SIZE.SMALL,
                length: LENGTH.THIN,
              }}
            />
            <TooltipEl
              attrs={{
                placement: Placement.Top,
                text: `${valueFormatter(item[context.field], {
                  isAverage: false,
                })} ${LIFT_TOOLTIP_COMPARE} ${valueFormatter(liftAbsoluteValue, {isAverage: false})}`,
              }}
            />
          </div>
        </>
      );
    }

    return valueFormatter(item[context.field], {isAverage: false});
  };
}
